import { API_ENDPOINTS } from '@/config';
import { api } from './apiClient';
import axios from 'axios';

export interface QuestionExample {
  input: Record<string, unknown>;
  output: number | string | unknown[];
  explanation: string;
}

export interface Question {
  id: number;
  title: string;
  problem_statement: string;
  input_format: string;
  output_format: string;
  examples: QuestionExample[];
  constraints: string[];
  status: 'Submitted' | 'Not Submitted' | 'Pending';
}

export interface CodingRoundData {
  startTime: string;
  endTime: string;
  questions: Question[];
}

export interface SubmitQuestionRequest {
  candidate_id: string;
  question_id: number;
  code: string;
  language: string;
}

interface SubmitQuestionResponse {
  status: string;
}

interface ExecuteCodeRequest {
  language: string;
  version: string;
  files: { content: string }[];
  stdin?: string;
}

interface ExecuteCodeResponse {
  run: {
    stdout: string;
    stderr: string;
    output: string;
    code: number;
    signal: string | null;
  };
}

interface PistonRuntime {
  language: string;
  version: string;
  aliases: string[];
  runtime?: string;
}

interface Judge0Language {
  id: number;
  name: string;
}

interface Judge0SubmissionRequest {
  language_id: number;
  source_code: string;
  stdin?: string;
}

interface Judge0SubmissionResponse {
  stdout: string | null;
  stderr: string | null;
  compile_output: string | null;
  message: string | null;
  status: {
    id: number;
    description: string;
  };
  time: string | null;
  memory: number | null;
}

export const codingRoundService = {
  getQuestions: async (candidateId: string): Promise<CodingRoundData> => {
    return await api.recruitmentAgent.post<CodingRoundData>(
      API_ENDPOINTS.codingRound.startTest,
      {
        candidate_id: candidateId,
      }
    );
  },

  submitQuestion: async (
    params: SubmitQuestionRequest
  ): Promise<SubmitQuestionResponse> => {
    return await api.recruitmentAgent.post<SubmitQuestionResponse>(
      API_ENDPOINTS.codingRound.submitAndEvaluate,
      params
    );
  },

  executeCode: async (
    params: ExecuteCodeRequest
  ): Promise<ExecuteCodeResponse> => {
    // Map language names to Judge0 language IDs
    const languageIdMap: Record<string, number> = {
      c: 50,
      cpp: 54,
      java: 62,
      javascript: 63,
      python: 71,
      csharp: 51,
    };

    const languageId = languageIdMap[params.language.toLowerCase()];
    if (!languageId) {
      throw new Error(`Unsupported language: ${params.language}`);
    }

    const sourceCode = params.files[0]?.content || '';

    const submissionRequest: Judge0SubmissionRequest = {
      language_id: languageId,
      source_code: sourceCode,
      stdin: params.stdin || undefined,
    };

    // Use Judge0 public API with wait=true for synchronous execution
    const response = await axios.post<Judge0SubmissionResponse>(
      API_ENDPOINTS.codingRound.judge0Submissions,
      submissionRequest,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const result = response.data;

    // Map Judge0 response to Piston-compatible format
    // Judge0 status.id: 3 = Accepted (success), others = errors
    const isSuccess = result.status.id === 3;
    const stdout = result.stdout || '';
    const stderr = result.stderr || result.compile_output || '';

    // Combine error messages if present
    let errorOutput = stderr;
    if (result.message && !isSuccess) {
      errorOutput = errorOutput
        ? `${errorOutput}\n${result.message}`
        : result.message;
    }

    return {
      run: {
        stdout,
        stderr: errorOutput,
        output: stdout || errorOutput,
        code: isSuccess ? 0 : 1,
        signal: null,
      },
    };
  },

  getPistonRuntimes: async (): Promise<PistonRuntime[]> => {
    // Fetch Judge0 languages and map to PistonRuntime format
    const response = await axios.get<Judge0Language[]>(
      API_ENDPOINTS.codingRound.judge0Languages
    );

    const languageMap: Record<string, string> = {
      '50': 'c',
      '54': 'cpp',
      '62': 'java',
      '63': 'javascript',
      '71': 'python',
      '51': 'csharp',
    };

    return response.data
      .filter(lang => languageMap[lang.id.toString()])
      .map(lang => ({
        language: languageMap[lang.id.toString()],
        version: 'latest',
        aliases: [lang.name.toLowerCase()],
      }));
  },
};
